import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sprout, Moon, Waves, Wind, Leaf, Compass } from "lucide-react";
import SoulHero from "@/components/soul/SoulHero";
import SoulPracticeCard from "@/components/soul/SoulPracticeCard";
import SoulBreadcrumb from "@/components/soul/SoulBreadcrumb";
import N8Emblem from "@/components/N8Emblem";
import { INNER_PRACTICES } from "@/pages/soul/soulContent";
import { HERO } from "@/lib/heroImages";

// Small lucide mapping — every practice gets a distinct copper icon.
const ICONS = {
  sprout: Sprout,
  moon: Moon,
  reflect: Waves,
  release: Wind,
  leaf: Leaf,
  threshold: Compass,
};

export default function InnerExploration() {
  const nav = useNavigate();
  return (
    <div className="space-y-12" data-testid="soul-inner-exploration">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SoulBreadcrumb
          items={[
            { label: "Rituals", to: "/rituals" },
            { label: "Soul", to: "/soul" },
            { label: "Inner Exploration" },
          ]}
        />
        <button
          type="button"
          onClick={() => nav("/soul")}
          data-testid="soul-inner-back"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))] transition-colors"
        >
          <ArrowLeft size={12} /> Back to Soul
        </button>
      </div>

      <SoulHero
        emblem=""
        eyebrow="Soul · Inner Exploration"
        title="Meet what lives beneath the surface."
        supporting="There is more to you than what is visible."
        body="These experiences invite you to explore the patterns, emotions, beliefs, memories, and inner knowing that shape the way you move through life. Not to fix yourself. To understand yourself."
        image={HERO.innerExploration}
        testid="soul-inner-hero"
      />

      <section
        className="reveal reveal-1 grid gap-8 md:grid-cols-2"
        data-testid="soul-inner-practices"
      >
        <div
          className="md:col-span-2 flex items-center gap-3 mb-2"
          data-testid="soul-inner-emblem-wrap"
        >
          <N8Emblem
            mode="full"
            size={52}
            ariaLabel="N8ONE emblem"
            testid="soul-inner-emblem"
          />
          <span className="text-[10.5px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
            Soul Work · Choose Your Path
          </span>
        </div>
        {INNER_PRACTICES.map((p) => (
          <SoulPracticeCard
            key={p.slug}
            title={p.title}
            tagline={p.tagline}
            image={p.image}
            to={`/soul/inner-exploration/${p.slug}`}
            Icon={ICONS[p.icon] || Sprout}
            testid={`soul-practice-${p.slug}`}
          />
        ))}
      </section>

      <section
        className="relative overflow-hidden rounded-[28px] px-6 py-16 md:px-14 md:py-24 text-center"
        data-testid="soul-inner-reflection"
      >
        <div
          aria-hidden
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage: `url("${HERO.soulReflection}")`,
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(30,23,18,0.55) 0%, rgba(60,42,32,0.72) 100%)",
          }}
        />
        <div className="relative z-10 max-w-3xl mx-auto">
          <p
            className="font-display italic text-2xl md:text-4xl leading-tight"
            style={{ color: "#F6F1E8" }}
          >
            The deeper you go inward,
            <br />
            the more of yourself you get to meet.
          </p>
          <p
            className="mt-6 text-[11px] uppercase tracking-[0.32em]"
            style={{ color: "#E19271" }}
          >
            And that changes everything
          </p>
        </div>
      </section>
    </div>
  );
}
